import { API_BASE_URL } from '../config';

export type PublishingPlatform = 'instagram' | 'youtube' | 'tiktok';
export type PublishingScheduleStatus =
  | 'preparing'
  | 'scheduled'
  | 'queued'
  | 'publishing'
  | 'published'
  | 'failed';
export type PublishingSchedulePrivacyStatus = 'private' | 'unlisted' | 'public';

export interface PublishingScheduleEntry {
  id: string;
  platform: PublishingPlatform;
  scheduledFor: string;
  filename: string;
  projectId?: string | null;
  projectName?: string | null;
  /** The cut's thumbnail, resolved by the backend for calendar and rail rows. */
  projectThumbnail?: string | null;
  releaseId?: string | null;
  releaseName?: string | null;
  caption: string;
  title: string;
  description: string;
  tags: string[];
  privacyStatus: PublishingSchedulePrivacyStatus;
  madeForKids: boolean;
  notifySubscribers: boolean;
  tiktokDirectPost: boolean;
  tiktokPrivacyLevel?: string | null;
  tiktokAllowComment?: boolean;
  tiktokAllowDuet?: boolean;
  tiktokAllowStitch?: boolean;
  tiktokBrandOrganic?: boolean;
  tiktokBrandedContent?: boolean;
  status: PublishingScheduleStatus;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  publishedUrl: string | null;
  lastError?: string | null;
  queuedJobId?: string | null;
  failedJobId?: string | null;
}

export interface CreatePublishingScheduleEntryInput {
  publishNow?: boolean;
  requestId?: string;
  platform: PublishingPlatform;
  scheduledFor: string;
  // The backend chooses the exported Media for this Cut.
  projectId: string;
  projectName?: string;
  releaseId?: string;
  releaseName?: string;
  caption?: string;
  title?: string;
  description?: string;
  tags?: string[];
  privacyStatus?: PublishingSchedulePrivacyStatus;
  madeForKids?: boolean;
  notifySubscribers?: boolean;
  tiktokDirectPost?: boolean;
  tiktokPrivacyLevel?: string | null;
  tiktokAllowComment?: boolean;
  tiktokAllowDuet?: boolean;
  tiktokAllowStitch?: boolean;
  tiktokBrandOrganic?: boolean;
  tiktokBrandedContent?: boolean;
}

export interface UpdatePublishingScheduleEntryInput {
  platform?: PublishingPlatform;
  scheduledFor?: string;
  filename?: string;
  projectId?: string | null;
  projectName?: string | null;
  releaseId?: string | null;
  releaseName?: string | null;
  caption?: string;
  title?: string;
  description?: string;
  tags?: string[];
  privacyStatus?: PublishingSchedulePrivacyStatus;
  madeForKids?: boolean;
  notifySubscribers?: boolean;
  tiktokDirectPost?: boolean;
  tiktokPrivacyLevel?: string | null;
  tiktokAllowComment?: boolean;
  tiktokAllowDuet?: boolean;
  tiktokAllowStitch?: boolean;
  tiktokBrandOrganic?: boolean;
  tiktokBrandedContent?: boolean;
}

/** What the creator chose for a TikTok direct post. */
export interface TikTokPostSettingsInput {
  tiktokPrivacyLevel: string;
  tiktokAllowComment: boolean;
  tiktokAllowDuet: boolean;
  tiktokAllowStitch: boolean;
  tiktokBrandOrganic: boolean;
  tiktokBrandedContent: boolean;
}

export interface PublishScheduleEntryResponse {
  success: boolean;
  queued: boolean;
  jobId?: string | null;
  entry: PublishingScheduleEntry;
}

const readErrorDetail = async (response: Response, fallback: string): Promise<string> => {
  try {
    const payload = await response.json();
    if (typeof payload?.detail === 'string' && payload.detail.trim()) {
      return payload.detail;
    }
    if (typeof payload?.error === 'string' && payload.error.trim()) {
      return payload.error;
    }
  } catch {
    // Ignore JSON parsing failures and use the fallback.
  }
  return fallback;
};

const PUBLISHING_PLATFORMS: PublishingPlatform[] = ['youtube', 'instagram', 'tiktok'];

export interface PublishingAvailability {
  platforms: PublishingPlatform[];
  planIncluded: boolean;
  /** TikTok posts go straight to the profile; otherwise they arrive in the creator's TikTok inbox as drafts. */
  tiktokDirectPost: boolean;
}

/** The launched platforms for this account, and whether its plan lets it connect and publish to them. */
export async function getPublishingAvailability(userId: string): Promise<PublishingAvailability> {
  const url = new URL(`${API_BASE_URL}/api/publishing/availability`);
  url.searchParams.set('userId', userId);
  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(await readErrorDetail(response, 'Failed to load publishing availability'));
  }
  const payload: { platforms?: unknown; planIncluded?: unknown; tiktokDirectPost?: unknown } = await response.json();
  const platforms = Array.isArray(payload.platforms) ? payload.platforms : [];
  return {
    platforms: PUBLISHING_PLATFORMS.filter((platform) => platforms.includes(platform)),
    planIncluded: payload.planIncluded === true,
    tiktokDirectPost: payload.tiktokDirectPost === true,
  };
}

export interface PublishingScheduleWindow {
  start: string;
  end: string;
}

export async function listPublishingSchedule(
  userId: string,
  window?: PublishingScheduleWindow,
): Promise<PublishingScheduleEntry[]> {
  const url = new URL(`${API_BASE_URL}/api/publishing/schedule`);
  url.searchParams.set('userId', userId);
  const now = Date.now();
  url.searchParams.set('start', window?.start ?? new Date(now - 30 * 86400000).toISOString());
  url.searchParams.set('end', window?.end ?? new Date(now + 30 * 86400000).toISOString());
  const pageSize = 100;
  url.searchParams.set('limit', String(pageSize));
  const entries = new Map<string, PublishingScheduleEntry>();
  for (let offset = 0; ; offset += pageSize) {
    url.searchParams.set('offset', String(offset));
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(await readErrorDetail(response, 'Failed to load publishing schedule'));
    }
    const page: PublishingScheduleEntry[] = await response.json();
    const previousSize = entries.size;
    page.forEach((entry) => entries.set(entry.id, entry));
    if (page.length < pageSize) return Array.from(entries.values());
    if (entries.size === previousSize) throw new Error('Failed to load publishing schedule');
  }
}

export async function listPublishingSuggestions(userId: string): Promise<string[]> {
  const url = new URL(`${API_BASE_URL}/api/publishing/suggestions`);
  url.searchParams.set('userId', userId);
  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(await readErrorDetail(response, 'Failed to load suggested cuts'));
  }
  return response.json();
}

export async function createPublishingScheduleEntry(
  userId: string,
  input: CreatePublishingScheduleEntryInput,
): Promise<PublishingScheduleEntry> {
  const response = await fetch(`${API_BASE_URL}/api/publishing/schedule`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userId,
      ...input,
    }),
  });

  if (!response.ok) {
    throw new Error(await readErrorDetail(response, 'Failed to create scheduled post'));
  }

  return response.json();
}

export async function updatePublishingScheduleEntry(
  userId: string,
  entryId: string,
  changes: UpdatePublishingScheduleEntryInput,
): Promise<PublishingScheduleEntry> {
  const response = await fetch(`${API_BASE_URL}/api/publishing/schedule/${encodeURIComponent(entryId)}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userId,
      ...changes,
    }),
  });

  if (!response.ok) {
    throw new Error(await readErrorDetail(response, 'Failed to update scheduled post'));
  }

  return response.json();
}

export async function deletePublishingScheduleEntry(userId: string, entryId: string): Promise<void> {
  const url = new URL(`${API_BASE_URL}/api/publishing/schedule/${encodeURIComponent(entryId)}`);
  url.searchParams.set('userId', userId);

  const response = await fetch(url.toString(), {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error(await readErrorDetail(response, 'Failed to delete scheduled post'));
  }
}

export async function publishPublishingScheduleEntry(
  userId: string,
  entryId: string,
  force: boolean = true,
): Promise<PublishScheduleEntryResponse> {
  const response = await fetch(`${API_BASE_URL}/api/publishing/schedule/${encodeURIComponent(entryId)}/publish`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      userId,
      force,
    }),
  });

  if (!response.ok) {
    throw new Error(await readErrorDetail(response, 'Failed to queue publish job'));
  }

  return response.json();
}
